import React from 'react'
import { Toggle } from '../../legacy-design-system/toggle'
import { WithTooltip } from '../../design-system'
import { theme as deprecatedTheme } from '../../styled/theme'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { css, cx } from '@emotion/css'
import { bodyCss, readOnlyGridCss } from '../../legacy-design-system/text'

interface ToggleStatus {
  readonly channelKey: string
  readonly disabled: boolean
  readonly enabled: boolean
  readonly updating: boolean
}

interface Entry {
  readonly db: ToggleStatus
  readonly email: ToggleStatus
  readonly webhook?: ToggleStatus
  readonly type: string
}

interface Props {
  readonly entry: Entry
  readonly title: string
  readonly subtitle: string
  readonly fieldRequired?: boolean
  readonly tooltipContent?: React.ReactNode
  readonly onToggle: (entry: Entry, channelKey: string) => void
}

function getColumnWidth(webhookEntry: boolean): number {
  return webhookEntry
    ? 50 // 50%
    : 200 / 3 // 66.66%
}

const toggleContainerWidth = 100 / 6 // 16.16%

export function NotificationSubscriptionRow({
  entry,
  title,
  subtitle,
  fieldRequired = false,
  tooltipContent,
  onToggle
}: Props) {
  return (
    <div
      className={css`
        display: flex;
        flex-direction: row;
        height: 5.5em;
        align-items: center;
      `}
    >
      <div
        className={css`
          display: flex;
          flex-direction: column;
          width: ${getColumnWidth(!!entry.webhook)}%;
          height: fit-content;
          padding: 0px 15px 0px 15px;
        `}
      >
        <div
          className={css`
            display: flex;
            flex-direction: row;
            align-items: center;
          `}
        >
          <span
            className={cx(
              bodyCss,
              css`
                fontsize: '15px';
              `
            )}
          >
            {title}
          </span>

          {fieldRequired && (
            <WithTooltip
              placement={'right'}
              renderTooltipContent={() => tooltipContent}
              children={() => (
                <span
                  className={css`
                    margin-left: 3px;
                    color: ${theme.color.turquoise70};
                    font-size: 17px;
                    font-family: ${deprecatedTheme.fontFamilies.secondary};
                    font-weight: ${deprecatedTheme.fontWeights.primary.bold};
                  `}
                >
                  *
                </span>
              )}
            />
          )}
        </div>
        <div className={readOnlyGridCss}>{subtitle}</div>
      </div>

      {!!entry.webhook && (
        <div
          className={css`
            display: flex;
            flex-direction: column;
            width: ${toggleContainerWidth}%;
            height: 5.5em;
            border-right: 1px solid ${theme.color.neutral30};
            justify-content: center;
            align-items: center;
          `}
        >
          <Toggle
            checked={entry.webhook.enabled}
            onChange={() =>
              !!entry.webhook
                ? onToggle(entry, entry.webhook.channelKey)
                : undefined
            }
            disabled={entry.webhook.updating || entry.webhook.disabled}
          />
        </div>
      )}

      <div
        className={css`
          display: flex;
          flex-direction: column;
          background-color: ${theme.color.white};
          width: ${toggleContainerWidth}%;
          height: 5.5em;
          border-right: 1px solid ${theme.color.neutral30};
          justify-content: center;
          align-items: center;
        `}
      >
        {
          <Toggle
            checked={entry.db.enabled}
            onChange={() => onToggle(entry, entry.db.channelKey)}
            disabled={entry.db.updating || entry.db.disabled}
          />
        }
      </div>

      <div
        className={css`
          display: flex;
          flex-direction: column;
          background-color: ${theme.color.white};
          width: ${toggleContainerWidth}%;
          height: 5.5em;
          justify-content: center;
          align-items: center;
        `}
      >
        {
          <Toggle
            checked={entry.email.enabled}
            onChange={() => onToggle(entry, entry.email.channelKey)}
            disabled={entry.email.updating || entry.email.disabled}
          />
        }
      </div>
    </div>
  )
}
